import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { Observable } from 'rxjs'
import { map, take, withLatestFrom } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { UtilityService } from 'src/app/core/utility.service'
import { ResourceService } from 'src/app/shared/resource-service'
import { environment } from 'src/environments/environment'
import { Member } from '../../member/member'
import { organizationSelectors } from '../../organization/organization.selectors'
import { StatusCode } from '../../sale/sale'
import { saleItemActions } from '../../sale/sale-item/sale-item.actions'
import { saleItemSelectors } from '../../sale/sale-item/sale-item.selectors'
import { saleActions } from '../../sale/sale.actions'
import { SerieProposal, SerieProposalStatus } from './serie-proposal'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from './serie-proposal-item/serie-proposal-item'
import { serieProposalItemSelectors } from './serie-proposal-item/serie-proposal-item.selectors'
import { SerieProposalSerializer } from './serie-proposal.serializer'
import { TypeOfProduct } from 'src/app/shared/constants'

@Injectable()
export class SerieProposalService extends ResourceService<SerieProposal> {
  constructor(
    private http: HttpClient,
    private utilityService: UtilityService,
    private serieProposalSerializer: SerieProposalSerializer,
    private store: Store<AppState>,
    private router: Router
  ) {
    super(
      http,
      `${environment.backendApiUrl}/serie-proposals`,
      serieProposalSerializer
    )
  }
  private extractStartAndFinalHour(
    serieProposalBookings: any[],
    dependantProductProposalsBookings: any[],
    hour
  ) {
    let minStartTime = hour
    let maxEndTime = String(parseInt(hour) + 1) + ':00'
    if (
      serieProposalBookings.length == 0 &&
      dependantProductProposalsBookings.length == 0
    ) {
      return {
        defaultStartHour: minStartTime,
        defaultEndHour: maxEndTime
      }
    }

    const combinedData = [
      ...serieProposalBookings,
      ...dependantProductProposalsBookings
    ]

    combinedData.forEach((it) => {
      if (
        new Date(`1970-01-01T${it.endTime}Z`) >
        new Date(`1970-01-01T${maxEndTime}:00Z`)
      ) {
        maxEndTime = it.endTime
      }
    })

    return {
      defaultStartHour: minStartTime,
      defaultEndHour: maxEndTime
    }
  }

  public getProposalsForProductAndHour(
    productId: number,
    hour: any,
    productEntities: any,
    selectedDay: any,
    serieProposals: any,
    dependantProductsMap?: any,
    seriesProporsalItemEntities?: any
  ): any[] {
    if (!seriesProporsalItemEntities) return []

    const serieProposalStatus = SerieProposalStatus
    let proposals = []
    let proposalsDependant = []
    let proposalsNoDependant = []
    const selectedDayOfWeek = selectedDay.isoWeekday()
    const currentHour = parseInt(hour)
    const productProposals = serieProposals.filter(
      (proposal) => proposal.productId == productId
    )
    // TODO: when checking the conflicts we have to check the same period
    // so if there is a proposal for Wed 09:00-10:00 with startDate 1 Nov and endDate 30 Nov
    // and there is another for Wed 09:00-10:00 with startDate 1 Dec and endDate 31 Dec => it is not conflicting

    const dependantProductsOfProduct = this.getDependantProductIdsByProductId(
      productId,
      productEntities
    )?.filter((ite) => Object.keys(dependantProductsMap)?.includes(String(ite)))
    const dependantProductProposals = serieProposals?.filter((proposal) =>
      dependantProductsOfProduct?.includes(proposal.productId)
    )
    const serieProposalBookings = []
    let dependantProductProposalsBookings = []
    // create booking from series Proposal
    serieProposals.forEach((sP) => {
      let bookings = []

      if (sP.details.initialRequest.repeatableBookings?.length > 0) {
        if (sP.details.repeatableBookings?.length > 0) {
          bookings = sP.details.repeatableBookings
        } else {
          bookings = sP.details.initialRequest.repeatableBookings
        }
      } else if (sP.details.initialRequest.customBookings?.length > 0) {
        if (sP.details.customBookings?.length > 0) {
          bookings = sP.details.customBookings
        } else {
          bookings = sP.details.initialRequest.customBookings
        }
      }
      bookings.forEach((booking) => {
        if (
          (booking.dayOfWeek && booking.dayOfWeek === selectedDayOfWeek) ||
          (booking.startDate &&
            moment(booking.startDate).isoWeekday() === selectedDayOfWeek)
        ) {
          serieProposalBookings.push({
            id: sP.id,
            dayOfWeek:
              booking.dayOfWeek || moment(booking.startDate).isoWeekday(),
            startTime:
              booking?.startTime || moment(booking?.startDate)?.format('HH:mm'),
            endTime:
              booking?.endTime || moment(booking?.endDate)?.format('HH:mm'),
            productId: sP.productId,
            linkedProductId: sP.linkedProductId
          })
        }
      })
    })
    //Check dependant and create bookings from dependant  proposal
    dependantProductProposals.forEach((sP) => {
      let bookings = []

      if (sP.details.initialRequest.repeatableBookings?.length > 0) {
        if (sP.details.repeatableBookings?.length > 0) {
          bookings = sP.details.repeatableBookings
        } else {
          bookings = sP.details.initialRequest.repeatableBookings
        }
      } else if (sP.details.initialRequest.customBookings?.length > 0) {
        if (sP.details.customBookings?.length > 0) {
          bookings = sP.details.customBookings
        } else {
          bookings = sP.details.initialRequest.customBookings
        }
      }

      for (const booking of bookings) {
        const conflictingProposals = serieProposalBookings
          .filter((p) => {
            const { startTime: startBooking, endTime: endBooking } = booking
            const { startTime: startCurrent, endTime: endCurrent } = p

            const isTimeOverlap =
              startCurrent < endBooking && endCurrent > startBooking

            return (
              p.dayOfWeek === selectedDayOfWeek &&
              isTimeOverlap &&
              (p.productId === sP.productId ||
                (dependantProductsMap[productId] &&
                  dependantProductsMap[productId].includes(sP.productId)))
            )
          })
          .sort((a, b) => a.startTime.localeCompare(b.startTime))
        conflictingProposals.forEach((cP) => {
          dependantProductProposalsBookings.push({
            id: cP.id,
            dayOfWeek: cP.dayOfWeek,
            startTime: cP.startTime,
            endTime: cP.endTime,
            productId: cP.productId,
            linkedProductId: cP.linkedPsroductId
          })
        })
      }
    })
    //remove repeated and filter for hour
    dependantProductProposalsBookings = dependantProductProposalsBookings
      .filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.id === value.id &&
              t.dayOfWeek === value.dayOfWeek &&
              t.startTime === value.startTime &&
              t.endTime === value.endTime &&
              t.productId === value.productId
          )
      )
      .filter((it) => dependantProductsOfProduct.includes(it.productId))
    const { defaultStartHour, defaultEndHour } = this.extractStartAndFinalHour(
      serieProposalBookings.filter((it) => {
        const startTime = new Date(`1970-01-01T${it.startTime}Z`).getTime()
        const endTime = new Date(`1970-01-01T${it.endTime}Z`).getTime()
        const cInitialHour = new Date(`1970-01-01T${hour}:00Z`).getTime()

        return (
          it.dayOfWeek === selectedDayOfWeek &&
          cInitialHour >= startTime &&
          cInitialHour <= endTime
        )
      }),
      dependantProductProposalsBookings.filter((it) => {
        const startTime = new Date(`1970-01-01T${it.startTime}Z`).getTime()
        const endTime = new Date(`1970-01-01T${it.endTime}Z`).getTime()
        const cInitialHour = new Date(`1970-01-01T${hour}:00Z`).getTime()

        return (
          it.dayOfWeek === selectedDayOfWeek &&
          (parseInt(it.startTime) == currentHour ||
            (cInitialHour >= startTime && cInitialHour <= endTime))
        )
      }),
      hour
    )
    const currentHourDependantProductProposalsBookings =
      dependantProductProposalsBookings.filter((it) => {
        const startTime = new Date(`1970-01-01T${it.startTime}Z`).getTime()
        const endTime = new Date(`1970-01-01T${it.endTime}Z`).getTime()
        const cInitialHour = new Date(
          `1970-01-01T${defaultStartHour}Z`
        ).getTime()
        const cFinalHour = new Date(`1970-01-01T${defaultEndHour}Z`).getTime()

        return (
          parseInt(it.startTime) == currentHour ||
          (startTime < cFinalHour && cInitialHour < endTime)
        )
      })
    let overlappingDependantProductProposalsBookings = []
    currentHourDependantProductProposalsBookings.forEach((item) => {
      const refStart = item.startTime
      const refEnd = item.endTime
      const overlappingItems = dependantProductProposalsBookings.filter(
        (it) => {
          const itemStart = it.startTime
          const itemEnd = it.endTime

          return itemStart < refEnd && itemEnd > refStart
        }
      )

      overlappingDependantProductProposalsBookings = [
        ...overlappingDependantProductProposalsBookings,
        ...overlappingItems
      ]
    })
    overlappingDependantProductProposalsBookings =
      overlappingDependantProductProposalsBookings.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.id === value.id)
      )
    //insert dependantProducs in saleProposal array
    overlappingDependantProductProposalsBookings
      // .filter((it) => it.productId != productId)
      .forEach((it, idx) => {
        proposalsDependant.push({
          ...it,
          serieProposalItems: [],
          messages: null,
          hasConflict: false,
          organizationId: null,
          linkedProductId: null,
          productId: productId,
          status: null,
          group: 0,
          finalStatus: null,
          blocked: true
        })
      })
    productProposals.forEach((proposal) => {
      const repetableBookings = proposal.details.repeatableBookings.length
        ? proposal.details.repeatableBookings
        : proposal.details.initialRequest.repeatableBookings

      if (repetableBookings.length) {
        const bookings = repetableBookings.filter((booking) => {
          const startTime = new Date(
            `1970-01-01T${booking.startTime}Z`
          ).getTime()
          const endTime = new Date(`1970-01-01T${booking.endTime}Z`).getTime()
          const cInitialHour = new Date(
            `1970-01-01T${defaultStartHour}Z`
          ).getTime()
          const cFinalHour = new Date(`1970-01-01T${defaultEndHour}Z`).getTime()

          return (
            booking.dayOfWeek == selectedDayOfWeek &&
            (parseInt(booking.startTime) == currentHour ||
              (startTime < cFinalHour && cInitialHour < endTime))
          )
        })

        for (const booking of bookings) {
          const bookingStartTime = moment(booking.startTime, 'HH:mm:ss')
          const bookingEndTime = moment(booking.endTime, 'HH:mm:ss')
          let hasConflict = false

          let conflictingProposals = serieProposalBookings
            .filter((p) => {
              const { startTime: startBooking, endTime: endBooking } = booking
              const { startTime: startCurrent, endTime: endCurrent } = p

              const isTimeOverlap =
                startCurrent < endBooking && endCurrent > startBooking

              return (
                p.dayOfWeek === selectedDayOfWeek &&
                isTimeOverlap &&
                p.productId === proposal.productId
              )
            })
            .sort((a, b) => a.startTime.localeCompare(b.startTime))

          // conflictingProposals.forEach((p, index) => {
          //   p.hasConflict = p.id != proposal.id ? true : false
          // })
          conflictingProposals.forEach((p, index) => {
            p.hasConflict = true
          })

          hasConflict =
            conflictingProposals.filter((it) => it.hasConflict).length > 1

          const dependantConflicts = dependantProductProposals.some(
            (dependantProposal) => {
              let hasDependantConflict = false
              const dpRepetableBookings = dependantProposal.details
                .repeatableBookings.length
                ? dependantProposal.details.repeatableBookings
                : dependantProposal.details.initialRequest.repeatableBookings

              // check conflicts in repeatable bookings
              if (dpRepetableBookings.length) {
                const dependantRepeatableBookings = dpRepetableBookings.filter(
                  (repeatableBooking) => {
                    const { startTime: startBooking, endTime: endBooking } =
                      booking
                    const { startTime: startCurrent, endTime: endCurrent } =
                      repeatableBooking

                    const isTimeOverlap =
                      startCurrent < endBooking && endCurrent > startBooking
                    const duration = this.getDuration(proposal)
                    return (
                      repeatableBooking.dayOfWeek == selectedDayOfWeek &&
                      (isTimeOverlap ||
                        (currentHour + duration >=
                          parseInt(repeatableBooking.startTime) &&
                          currentHour + duration <=
                            parseInt(repeatableBooking.endTime)))
                    )
                  }
                )

                hasDependantConflict = dependantRepeatableBookings.some(
                  (repeatableBooking) => {
                    const dependantProposalStartTime = moment(
                      repeatableBooking.startTime,
                      'HH:mm:ss'
                    )
                    const dependantProposalEndTime = moment(
                      repeatableBooking.endTime,
                      'HH:mm:ss'
                    )
                    return (
                      moment(bookingStartTime).isSameOrBefore(
                        dependantProposalEndTime
                      ) &&
                      moment(bookingEndTime).isSameOrAfter(
                        dependantProposalStartTime
                      ) &&
                      moment(bookingStartTime).format('HH:mm') !=
                        moment(dependantProposalEndTime).format('HH:mm') &&
                      moment(bookingEndTime).format('HH:mm') !=
                        moment(dependantProposalStartTime).format('HH:mm')
                    )
                  }
                )
              }
              const dpCustomBookings = dependantProposal.details.customBookings
                .length
                ? dependantProposal.details.customBookings
                : dependantProposal.details.initialRequest.customBookings

              if (!hasDependantConflict && dpCustomBookings.length) {
                const dependantCustomBookings = dpCustomBookings.filter(
                  (booking) => {
                    return (
                      moment(booking.startDate).isoWeekday() ==
                        selectedDayOfWeek &&
                      parseInt(moment(booking.startDate).format('HH')) ==
                        currentHour
                    )
                  }
                )

                hasDependantConflict = dependantCustomBookings.some(
                  (customBooking) => {
                    const dependantProposalStartTime = moment(
                      customBooking.startDate
                    ).format('HH:mm')
                    const dependantProposalEndTime = moment(
                      customBooking.endDate
                    ).format('HH:mm')

                    const bookingStart =
                      moment(bookingStartTime).format('HH:mm')
                    const bookingEnd = moment(bookingEndTime).format('HH:mm')

                    const customStart = moment(
                      dependantProposalStartTime,
                      'HH:mm'
                    )
                    const customEnd = moment(dependantProposalEndTime, 'HH:mm')
                    const bookingStartMoment = moment(bookingStart, 'HH:mm')
                    const bookingEndMoment = moment(bookingEnd, 'HH:mm')

                    return (
                      bookingStartMoment.isSameOrBefore(customEnd) &&
                      bookingEndMoment.isSameOrAfter(customStart) &&
                      moment(bookingStartTime).format('HH:mm') !=
                        moment(dependantProposalEndTime).format('HH:mm') &&
                      moment(bookingEndTime).format('HH:mm') !=
                        moment(dependantProposalStartTime).format('HH:mm')
                    )
                  }
                )
              }

              return hasDependantConflict
            }
          )

          hasConflict = hasConflict || dependantConflicts

          proposalsNoDependant.push({
            ...proposal,
            startTime: booking.startTime.substring(0, 5),
            endTime: booking.endTime.substring(0, 5),
            hasConflict,
            conflictGroup: conflictingProposals.length,
            positionInConflictGroup: conflictingProposals.findIndex(
              (it) => it.id == proposal.id
            ),
            group: 1
          })
        }
      }
      const customBookings = proposal.details.customBookings.length
        ? proposal.details.customBookings
        : proposal.details.initialRequest.customBookings
      if (customBookings.length) {
        const bookings = customBookings.filter(
          (booking) =>
            moment(booking.startDate).isoWeekday() == selectedDayOfWeek &&
            (parseInt(moment(booking.startDate).format('HH')) == currentHour ||
              (parseInt(moment(booking.startDate).format('HH')) < currentHour &&
                parseInt(moment(booking.startDate).format('HH')) > currentHour))
        )

        for (const booking of bookings) {
          const bookingStartTime = moment(booking.startDate).format('HH:mm')
          const bookingEndTime = moment(booking.endDate).format('HH:mm')
          const bookingStartMoment = moment(bookingStartTime, 'HH:mm')
          const bookingEndMoment = moment(bookingEndTime, 'HH:mm')
          let hasConflict = false

          for (const proposal of proposals) {
            const proposalStartTime = moment(proposal.startTime, 'HH:mm')
            const proposalEndTime = moment(proposal.endTime, 'HH:mm')

            if (
              bookingStartMoment.isSameOrBefore(proposalEndTime) &&
              bookingEndMoment.isSameOrAfter(proposalStartTime)
            ) {
              proposal['hasConflict'] = true
              hasConflict = true
            }
          }

          const dependantConflicts = dependantProductProposals.some((dp) => {
            let hasDependantConflict = false
            const dpRepeatableBookings = dp.details.repeatableBookings.length
              ? dp.details.repeatableBookings
              : dp.details.initialRequest.repeatableBookings
            if (dpRepeatableBookings.length) {
              const dependantRepeatableBookings = dpRepeatableBookings.filter(
                (repeatableBooking) =>
                  repeatableBooking.dayOfWeek == selectedDayOfWeek &&
                  (parseInt(repeatableBooking.startTime) == currentHour ||
                    (parseInt(repeatableBooking.startTime) < currentHour &&
                      parseInt(repeatableBooking.startTime) > currentHour))
              )

              hasDependantConflict = dependantRepeatableBookings.some(
                (repeatableBooking) => {
                  const dependantProposalStartTime = moment(
                    repeatableBooking.startTime,
                    'HH:mm:ss'
                  )
                  const dependantProposalEndTime = moment(
                    repeatableBooking.endTime,
                    'HH:mm:ss'
                  )
                  return (
                    bookingStartMoment.isSameOrBefore(
                      dependantProposalEndTime
                    ) &&
                    bookingEndMoment.isSameOrAfter(
                      dependantProposalStartTime
                    ) &&
                    moment(bookingStartTime).format('HH:mm') !=
                      moment(dependantProposalEndTime).format('HH:mm') &&
                    moment(bookingEndTime).format('HH:mm') !=
                      moment(dependantProposalStartTime).format('HH:mm')
                  )
                }
              )
            }
            const dpCustomBookings = dp.details.customBookings.length
              ? dp.details.customBookings
              : dp.details.initialRequest.customBookings
            if (!hasDependantConflict && dpCustomBookings.length) {
              const dependantCustomBookings = dpCustomBookings.filter(
                (customBooking) => {
                  return (
                    moment(customBooking.startDate).isoWeekday() ==
                      selectedDayOfWeek &&
                    parseInt(moment(customBooking.startDate).format('HH')) ==
                      currentHour
                  )
                }
              )

              hasDependantConflict = dependantCustomBookings.some(
                (customBooking) => {
                  const dependantProposalStartTime = moment(
                    customBooking.startDate
                  ).format('HH:mm')
                  const dependantProposalEndTime = moment(
                    customBooking.endDate
                  ).format('HH:mm')

                  const bookingStart = moment(bookingStartTime).format('HH:mm')
                  const bookingEnd = moment(bookingEndTime).format('HH:mm')

                  const customStart = moment(
                    dependantProposalStartTime,
                    'HH:mm'
                  )
                  const customEnd = moment(dependantProposalEndTime, 'HH:mm')
                  const bookingStartMoment = moment(bookingStart, 'HH:mm')
                  const bookingEndMoment = moment(bookingEnd, 'HH:mm')

                  return (
                    bookingStartMoment.isSameOrBefore(customEnd) &&
                    bookingEndMoment.isSameOrAfter(customStart) &&
                    moment(bookingStartTime).format('HH:mm') !=
                      moment(dependantProposalEndTime).format('HH:mm') &&
                    moment(bookingEndTime).format('HH:mm') !=
                      moment(dependantProposalStartTime).format('HH:mm')
                  )
                }
              )
            }

            return hasDependantConflict
          })

          hasConflict = hasConflict || dependantConflicts

          proposalsNoDependant.push({
            ...proposal,
            startTime: bookingStartTime,
            endTime: bookingEndTime,
            hasConflict
          })
        }
      }
    })

    proposals = this.mergeAllProposals(
      proposalsNoDependant.sort((a, b) =>
        a.startTime.localeCompare(b.startTime)
      ),
      proposalsDependant.sort((a, b) => a.startTime.localeCompare(b.startTime))
    )

    proposals = proposals
      ?.sort((a, b) => a.startTime.localeCompare(b.startTime))
      ?.filter((it) => parseInt(it.startTime) == currentHour)
    let finalProposalMapped = proposals?.map((item: any, index) => {
      let finalStatus = null

      if (!item.hasOwnProperty('finalStatus')) {
        const allStatuses = item.serieProposalItems
          .map((id) => seriesProporsalItemEntities[id]?.status)
          .filter((it) => it !== undefined && it !== null)

        finalStatus = serieProposals.status ?? null

        if (
          allStatuses.length > 0 &&
          allStatuses.every(
            (status) => status === serieProposalStatus.ACCEPTED
          ) &&
          item.status == serieProposalStatus.ACCEPTED
        ) {
          finalStatus = serieProposalStatus.ACCEPTED
        } else if (
          allStatuses.length > 0 &&
          allStatuses.every(
            (status) => status === serieProposalStatus.DECLINED
          ) &&
          item.status == serieProposalStatus.DECLINED
        ) {
          finalStatus = serieProposalStatus.DECLINED
        } else if (
          allStatuses.length > 0 &&
          allStatuses.every(
            (status) => status === serieProposalStatus.COUNTER_PROPOSAL
          ) &&
          item.status == serieProposalStatus.COUNTER_PROPOSAL
        ) {
          finalStatus = serieProposalStatus.COUNTER_PROPOSAL
        } else if (
          allStatuses.length > 0 &&
          allStatuses.every(
            (status) => status === serieProposalStatus.REQUESTED
          ) &&
          item.status == serieProposalStatus.REQUESTED
        ) {
          finalStatus = serieProposalStatus.REQUESTED
        } else {
          //mixed
          finalStatus = item.status
        }
      }

      return {
        ...item,

        finalStatus
      }
    })
    finalProposalMapped?.forEach((proposal) => {
      if (!proposal.finalStatus) {
        proposal.startTime = moment(proposal.startTime, 'HH:mm:ss').format(
          'HH:mm'
        )
        proposal.endTime = moment(proposal.endTime, 'HH:mm:ss').format('HH:mm')
      }
    })

    return finalProposalMapped.sort((a, b) => {
      const momentStartTime1 = moment(a.startTime, 'HH:mm:ss')
      const momentStartTime2 = moment(b.startTime, 'HH:mm:ss')
      if (momentStartTime1.isBefore(momentStartTime2)) return -1
      if (momentStartTime1.isAfter(momentStartTime2)) return 1

      const momentEndTime1 = moment(a.endTime, 'HH:mm:ss')
      const momentEndTime2 = moment(b.endTime, 'HH:mm:ss')
      if (momentEndTime1.isBefore(momentEndTime2)) return 1
      if (momentEndTime1.isAfter(momentEndTime2)) return -1

      return 0
    })
  }
  private mergeAllProposals(
    proposalsNoDependant: any[],
    proposalsDependant: any[]
  ) {
    let globalIndex = 0
    let globalConflictGroup = 0
    let proposals = []
    let uniqueProposalsProposalsNoDependant = []
    let uniqueProposalsProposalsDependant = []

    if (proposalsNoDependant?.length > 0) {
      for (const mappedProposal of proposalsNoDependant) {
        if (
          !uniqueProposalsProposalsNoDependant.find(
            (p) => p.id == mappedProposal.id
          )
        ) {
          uniqueProposalsProposalsNoDependant.push(mappedProposal)
        }
      }
    }
    if (proposalsDependant?.length > 0) {
      for (const mappedProposal of proposalsDependant) {
        if (
          !uniqueProposalsProposalsNoDependant.find(
            (p) => p.id == mappedProposal.id
          )
        ) {
          uniqueProposalsProposalsDependant.push(mappedProposal)
        }
      }
    }

    uniqueProposalsProposalsNoDependant.forEach((it) => {
      proposals.push({
        ...it,
        conflictGroup: it.globalConflictGroup,
        positionInConflictGroup: it.positionInConflictGroup
      })
      globalIndex =
        it.positionInConflictGroup < uniqueProposalsProposalsNoDependant?.length
          ? uniqueProposalsProposalsNoDependant?.length
          : globalIndex
      globalConflictGroup = it.conflictGroup
    })
    globalConflictGroup =
      globalConflictGroup + uniqueProposalsProposalsDependant.length
    //update conflictGroup
    proposals.forEach((it) => {
      it.conflictGroup = globalConflictGroup
    })

    uniqueProposalsProposalsDependant.forEach((it, index) => {
      proposals.push({
        ...it,
        conflictGroup: globalConflictGroup,
        positionInConflictGroup: globalIndex + index
      })
    })
    //check proposal and change positionInConflictGroup if it's necessary
    proposals = this.sortAndReassignPositions(proposals)

    return proposals
  }
  private sortAndReassignPositions(proposals) {
    const positions = proposals.map((prop) => prop.positionInConflictGroup)
    const sortedPositions = [...positions].sort((a, b) => a - b)
    let arePositionsConsecutive: boolean = true
    for (let i = 0; i < sortedPositions.length - 1; i++) {
      if (sortedPositions[i] + 1 !== sortedPositions[i + 1]) {
        arePositionsConsecutive = false // Si hay un salto en la secuencia
      }
    }
    if (arePositionsConsecutive) {
      proposals = proposals.sort((a, b) =>
        a.startTime.localeCompare(b.startTime)
      )
      proposals.forEach((prop, index) => {
        prop.positionInConflictGroup = index
        prop.conflictGroup =
          prop.conflictGroup < proposals?.length
            ? proposals?.length
            : prop.conflictGroup
      })
    } else {
      proposals.forEach((prop, index) => {
        prop.positionInConflictGroup =
          prop.positionInConflictGroup >= prop.conflictGroup
            ? prop.conflictGroup - 1
            : prop.positionInConflictGroup
        prop.conflictGroup =
          prop.conflictGroup < proposals?.length
            ? prop.conflictGroup
            : proposals?.length
      })
    }

    return proposals
  }

  public getDuration(proposal): number {
    const momentTime1 = moment(proposal.startTime, 'HH:mm:ss')
    const momentTime2 = moment(proposal.endTime, 'HH:mm:ss')

    const differenceInMinutes = momentTime2.diff(momentTime1, 'minutes')

    return Math.round(differenceInMinutes / 60)
  }
  private hasBlocked(preProposals: any, hour: string, productId: number): any {
    const preProposalsForHour = preProposals[hour][productId]
    if (!preProposalsForHour) {
      return null
    }
    const proprsalBlocked = []
    for (const proposal of preProposalsForHour) {
      if (proposal?.blocked) {
        proprsalBlocked.push(proposal)
      }
    }
    return proprsalBlocked
  }

  private getDependantProductIdsByProductId(
    productId: number,
    productEntities: any
  ): number[] {
    let dependantProductIds = []

    if (!productEntities?.[productId]?.dependantProducts) {
      return
    }

    dependantProductIds = productEntities?.[productId]?.dependantProducts

    return dependantProductIds
  }
  public createSaleItemsPartialsFromSerieProposal(
    serieProposal: SerieProposal,
    serieProposalItems: SerieProposalItem[],
    memberEntities: Dictionary<Member>
  ) {
    this.store.dispatch(saleItemActions.resetPartials())

    let organization = null
    if (serieProposal.organizationId) {
      this.store
        .select(organizationSelectors.selectById, serieProposal.organizationId)
        .pipe(take(1))
        .subscribe((org) => {
          organization = org
        })
    }

    const memberCategory =
      memberEntities[serieProposal.customerPersonId]?.category >= 0
        ? memberEntities[serieProposal.customerPersonId]?.category
        : null

    for (let serieProposalItem of serieProposalItems) {
      const startDate = moment(serieProposalItem.startDate)
      startDate.set({ second: 0, millisecond: 0 })

      const endDate = moment(serieProposalItem.endDate)
      endDate.set({ second: 0, millisecond: 0 })

      if (serieProposalItem.status != SerieProposalItemStatus.DECLINED) {
        this.store.dispatch(
          saleItemActions.createOnePartial({
            entity: {
              productId: serieProposalItem.linkedProductId,
              quantity: 1,
              customerCategory: organization?.category ?? memberCategory,
              params: {
                bookableLinkedProductId: serieProposalItem.linkedProductId,
                bookableProductId: serieProposalItem.productId,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                invitedMemberEmails: [],
                invitedGuests: [],
                invitedOthers: [],
                memberInvitesAmount: null,
                invitedGuestCount: null,
                invitedOtherCount: null,
                amount: null
              },
              serieProposalItemId: serieProposalItem.id,
              typeProduct: TypeOfProduct.BOOKING
            }
          })
        )
      }
    }

    return this.store.dispatch(
      saleActions.createOnePartial({
        entity: {
          statusCode: StatusCode.INFLIGHT,
          customerPersonId: serieProposal.customerPersonId,
          customerCategory: organization?.category ?? memberCategory,
          organizationId: organization?.id,
          posId: this.utilityService.getPosId()
        }
      })
    )
  }

  public getSaleId$(item: SerieProposal): Observable<number> {
    return this.store.select(serieProposalItemSelectors.selectEntities).pipe(
      withLatestFrom(this.store.select(saleItemSelectors.selectEntities)),
      take(1),
      map(([serieProposalItemEntities, saleItemEntities]) => {
        const saleItemId =
          serieProposalItemEntities[item.serieProposalItems?.[0]]?.saleItemId
        return saleItemEntities[saleItemId]?.saleId
      })
    )
  }

  public getConflictingBookingsBySerieProposalId$(serieProposalId: number) {
    return this.http.get<any>(
      `${environment.backendApiUrl}/serie-proposals/${serieProposalId}/check-conflicts`
    )
  }
}
